import type { CitationRef } from "@/types/ai";

// ============================================================================
// Valid Citation Tabs
// ============================================================================

/** Valid citation tabs that the frontend can handle */
const VALID_CITATION_TABS = new Set([
  "demographics",
  "appointments",
  "medical-notes",
  "measurements",
  "medications",
  "billing",
  "forms-documents",
]);

/** Pattern for [[ref:tab|label]] / [[ref:tab:id|label]]. Groups: 1=tab, 2=id?, 3=label. */
const CITATION_REF_PATTERN = /\[\[ref:([a-z-]+)(?::([^|]+))?\|([^\]]+)\]\]/g;

/** Replace [[ref:...|label]] markers with just the label. */
export function stripCitationRefs(text: string): string {
  return text.replace(
    CITATION_REF_PATTERN,
    (_match, _tab, _id, label) => label,
  );
}

/**
 * Clean a single suggestion string of leaked JSON structure, escape sequences,
 * and citation refs. Applied to suggestions before save so persisted data is
 * already clean.
 */
function cleanSuggestion(raw: unknown): string {
  let cleaned = String(raw).trim();
  // Strip leaked JSON array wrapping: ["foo"] → foo
  cleaned = cleaned.replace(/^\[\s*"/, "").replace(/"\s*\]$/, "");
  // Unescape JSON-encoded quotes/newlines
  cleaned = cleaned.replace(/\\"/g, '"').replace(/\\n/g, "\n");
  // Citations belong in the summary only
  cleaned = stripCitationRefs(cleaned);
  return cleaned.trim();
}

// ============================================================================
// Response Parsers
// ============================================================================

/**
 * Parses the AI response to extract summary and suggestions
 * Supports both JSON structured output and text format
 */
export function parseAISummaryResponse(response: string): {
  summary: string;
  suggestions: string[];
} {
  // Clean the response - remove markdown code blocks and extra whitespace
  let cleanedResponse = response.trim();

  // Remove markdown code blocks (```json ... ``` or ``` ... ```)
  cleanedResponse = cleanedResponse.replace(/^```(?:json)?\s*\n?/i, "");
  cleanedResponse = cleanedResponse.replace(/\n?```\s*$/i, "");
  cleanedResponse = cleanedResponse.trim();

  // Try to extract JSON from the response if there's extra text
  // Look for JSON object pattern: { "summary": ..., "suggestions": ... }
  // Use a more robust regex that handles nested structures
  const jsonMatch = cleanedResponse.match(
    /\{(?:[^{}]|(?:\{[^{}]*\}))*"summary"(?:[^{}]|(?:\{[^{}]*\}))*"suggestions"(?:[^{}]|(?:\{[^{}]*\}))*\}/,
  );
  if (jsonMatch) {
    cleanedResponse = jsonMatch[0];
  } else {
    // Fallback: try to find JSON object boundaries more simply
    const firstBrace = cleanedResponse.indexOf("{");
    const lastBrace = cleanedResponse.lastIndexOf("}");
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      cleanedResponse = cleanedResponse.substring(firstBrace, lastBrace + 1);
    }
  }

  // Try to parse as JSON first (structured output)
  try {
    const parsed = JSON.parse(cleanedResponse.trim());
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "summary" in parsed &&
      "suggestions" in parsed
    ) {
      return {
        summary: String(parsed.summary || "").trim(),
        suggestions: Array.isArray(parsed.suggestions)
          ? parsed.suggestions
              .map(cleanSuggestion)
              .filter((s: string) => s.length > 0 && s.length < 200)
              .slice(0, 4)
          : [],
      };
    }
  } catch (e) {
    // Not JSON, fall through to text parsing
    // Log error in development for debugging
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Failed to parse JSON response:",
        e,
        "Response:",
        cleanedResponse.substring(0, 200),
      );
    }
  }

  // Parse text format with markers
  const summaryMatch = response.match(
    /---SUMMARY---\s*([\s\S]*?)---SUGGESTIONS---/,
  );
  const suggestionsMatch = response.match(/---SUGGESTIONS---\s*([\s\S]*?)$/);

  if (summaryMatch && suggestionsMatch) {
    const summary = summaryMatch[1].trim();
    const suggestionsText = suggestionsMatch[1].trim();
    const suggestions = suggestionsText
      .split("\n")
      .map(cleanSuggestion)
      .filter((s) => s.length > 0 && s.length < 200)
      .slice(0, 4);

    return { summary, suggestions };
  }

  // Fallback: treat entire response as summary
  return { summary: response.trim(), suggestions: [] };
}

/**
 * Validates and cleans citations in AI-generated text.
 *
 * - Citations with invalid tab names are stripped (replaced with just the label)
 * - Appointment citations with invalid IDs are converted to simple appointment refs
 * - Note citations with invalid IDs are converted to simple note refs
 *
 * @param text - The text containing citations
 * @param validAppointmentIds - Optional set of valid appointment IDs
 * @param validNoteIds - Optional set of valid note IDs
 * @returns Text with invalid citations cleaned up
 */
export function validateCitations(
  text: string,
  validAppointmentIds?: Set<string>,
  validNoteIds?: Set<string>,
): string {
  return text.replace(CITATION_REF_PATTERN, (match, tab, itemId, label) => {
    // Check if tab is valid
    if (!VALID_CITATION_TABS.has(tab)) {
      // Invalid tab - just return the label text
      return label;
    }

    // For appointments with IDs, validate the ID exists
    if (tab === "appointments" && itemId) {
      if (validAppointmentIds && !validAppointmentIds.has(itemId)) {
        // Invalid appointment ID - convert to simple appointment ref
        return `[[ref:appointments|${label}]]`;
      }
    }

    // For medical notes with IDs, validate the ID exists
    if (tab === "medical-notes" && itemId) {
      if (validNoteIds && !validNoteIds.has(itemId)) {
        // Invalid note ID - convert to simple note ref
        return `[[ref:medical-notes|${label}]]`;
      }
    }

    // Citation is valid, keep it as-is
    return match;
  });
}

/**
 * Parses citations from AI response text
 * Returns the cleaned text and array of citations
 * Supports both [[ref:tab|label]] and [[ref:tab:item-id|label]] formats
 */
export function parseCitations(text: string): {
  text: string;
  citations: CitationRef[];
} {
  const citations: CitationRef[] = [];
  for (const match of Array.from(text.matchAll(CITATION_REF_PATTERN))) {
    const tab = match[1];
    const itemId = match[2]; // Optional item ID (e.g., appointment ID)
    const label = match[3];

    citations.push({
      tab,
      label,
      original: match[0],
      appointmentId: tab === "appointments" && itemId ? itemId : undefined,
    });
  }

  return { text, citations };
}
